import { FormattedInventoryInfo, InventoryNavigationParams } from '../types/InventoryTypes';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY, ValueWithDelta } from '../../types/widget/types';
import { LocaleManager } from '@nirvana/shared/i18n-locale/common';
import {
  GetInventoryInformationResponse,
  InventoryInformation,
  Status,
} from '@nirvana/shared/dtos/types/widgets/common';
import { TemplateState } from '../../types/common/types';
import { NavigationConstants, NavigationHelper, NavigationItem } from '@nirvana/shared/navigation';
import { BaseWidgetView } from '../../widget/BaseWidgetView';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { exitFullscreen, isBrowserFullscreen, onExitFullscreenError } from '../../utils/fullscreen.utils';
import { EntityRole, ManagementStatus, NetworkColumn } from '../../networkV2/networkAppContainer/helpers/types';
import { OperatingSystemType } from '@nirvana/shared/dtos/types/protected-entities/common';
import { EndpointType } from '@nirvana/shared/dtos/types/shared/filters';
import { Filter as GridFilter } from '../../grid/gridFilters/types';

export class InventoryView extends BaseWidgetView {
  public latestData: FormattedInventoryInfo;
  public shadow: any;
  protected $wrapper: HTMLElement;
  protected translations: any;
  private readonly localeManager: LocaleManager = new LocaleManager();
  private navigationHandlers: Array<(event: any) => void> = [];
  private networkDisplayedColumns: Array<NetworkColumn> = [
    NetworkColumn.NAME,
    NetworkColumn.COMPANY,
    NetworkColumn.MANAGEMENT_STATUS,
    NetworkColumn.LAST_SEEN,
    NetworkColumn.TYPE,
    NetworkColumn.ROLE,
    NetworkColumn.OS_TYPE,
    NetworkColumn.OS_VERSION,
    NetworkColumn.ENDPOINT_TYPE,
  ];

  getDomElements() {
    this.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$widgetTitle = this.shadow.querySelector('#inventoryTitle');
    this.dom.$winWorkstationsName = this.shadow.querySelector('#winWorkstationsName');
    this.dom.$winWorkstationsIconTooltip = this.shadow.querySelector('#winWorkstationsIconTooltip');
    this.dom.$winServersName = this.shadow.querySelector('#winServersName');
    this.dom.$winServersIconTooltip = this.shadow.querySelector('#winServersIconTooltip');
    this.dom.$macOSName = this.shadow.querySelector('#macOSName');
    this.dom.$macOSIconTooltip = this.shadow.querySelector('#macOSIconTooltip');
    this.dom.$linuxName = this.shadow.querySelector('#linuxName');
    this.dom.$linuxIconTooltip = this.shadow.querySelector('#linuxIconTooltip');
    this.dom.$physicalEndpointsName = this.shadow.querySelector('#physicalEndpointsName');
    this.dom.$physicalEndpointsIconTooltip = this.shadow.querySelector('#physicalEndpointsIconTooltip');
    this.dom.$virtualMachinesName = this.shadow.querySelector('#virtualMachinesName');
    this.dom.$virtualMachinesIconTooltip = this.shadow.querySelector('#virtualMachinesIconTooltip');
    this.dom.$containerHostsName = this.shadow.querySelector('#containerHostsName');
    this.dom.$containerHostsIconTooltip = this.shadow.querySelector('#containerHostsIconTooltip');
    this.dom.$containersName = this.shadow.querySelector('#containersName');
    this.dom.$containersIconTooltip = this.shadow.querySelector('#containersIconTooltip');
    this.dom.$inventoryBlocks = this.shadow.querySelectorAll('.inventory-block');
    this.dom.$inventory = this.shadow.querySelector('#inventory');
    this.getTranslations();
    this.setTranslatedStrings();
    this.setErrorTooltip();
  }

  addDrillDownHandlers(targets: Array<Target>, canDrillDown: boolean, availableExtraFeatures: string[] = []) {
    if (!NavigationHelper.shouldAddHandlers(this.dependencies.Ext)) {
      return;
    }

    const selectedTargetPath = targets[0].path;

    if (!selectedTargetPath) {
      throw new Error('Missing path from target.');
    }

    const isPartner = Application.Main.company.isPartner();

    if (!isPartner) {
      this.networkDisplayedColumns.splice(this.networkDisplayedColumns.indexOf(NetworkColumn.COMPANY), 1);
    }

    let navigationItems: Array<NavigationItem<InventoryNavigationParams>> = [
      {
        elements: [this.shadow.querySelector(`h2.widget-title`)],
        section: NavigationConstants.NETWORK_V2.NAME,
        params: {
          filters: this.getTitleFilters(targets, isPartner),
          treeDetails: {
            targetId: targets[0].companyId,
            type: Constants.Companies.TYPE_COMPANY,
            path: selectedTargetPath,
            parentId: '', // not required
          },
          displayedColumns: this.networkDisplayedColumns,
          recursive: true,
        },
      },
      {
        elements: [
          this.shadow.querySelector(`[gz-inventory="winWorkstations"] [gz-inventory-field="categoryName"]`),
          this.shadow.querySelector(`[gz-inventory="winWorkstations"] [gz-inventory-field="value"]`),
        ],
        section: NavigationConstants.NETWORK_V2.NAME,
        params: {
          filters: this.getWindowsWorkstationsFilters(targets, isPartner),
          treeDetails: {
            targetId: targets[0].companyId,
            type: Constants.Companies.TYPE_COMPANY,
            path: selectedTargetPath,
            parentId: '', // not required
          },
          displayedColumns: this.networkDisplayedColumns,
          recursive: true,
        },
      },
      {
        elements: [
          this.shadow.querySelector(`[gz-inventory="winServers"] [gz-inventory-field="categoryName"]`),
          this.shadow.querySelector(`[gz-inventory="winServers"] [gz-inventory-field="value"]`),
        ],
        section: NavigationConstants.NETWORK_V2.NAME,
        params: {
          filters: this.getWindowsServersFilters(targets, isPartner),
          treeDetails: {
            targetId: targets[0].companyId,
            type: Constants.Companies.TYPE_COMPANY,
            path: selectedTargetPath,
            parentId: '', // not required
          },
          displayedColumns: this.networkDisplayedColumns,
          recursive: true,
        },
      },
      {
        elements: [
          this.shadow.querySelector(`[gz-inventory="macOS"] [gz-inventory-field="categoryName"]`),
          this.shadow.querySelector(`[gz-inventory="macOS"] [gz-inventory-field="value"]`),
        ],
        section: NavigationConstants.NETWORK_V2.NAME,
        params: {
          filters: this.getMacOsFilters(targets, isPartner),
          treeDetails: {
            targetId: targets[0].companyId,
            type: Constants.Companies.TYPE_COMPANY,
            path: selectedTargetPath,
            parentId: '', // not required
          },
          displayedColumns: this.networkDisplayedColumns,
          recursive: true,
        },
      },
      {
        elements: [
          this.shadow.querySelector(`[gz-inventory="linux"] [gz-inventory-field="categoryName"]`),
          this.shadow.querySelector(`[gz-inventory="linux"] [gz-inventory-field="value"]`),
        ],
        section: NavigationConstants.NETWORK_V2.NAME,
        params: {
          filters: this.getLinuxFilters(targets, isPartner),
          treeDetails: {
            targetId: targets[0].companyId,
            type: Constants.Companies.TYPE_COMPANY,
            path: selectedTargetPath,
            parentId: '', // not required
          },
          displayedColumns: this.networkDisplayedColumns,
          recursive: true,
        },
      },
      {
        elements: [
          this.shadow.querySelector(`[gz-inventory="physicalEndpoints"] [gz-inventory-field="categoryName"]`),
          this.shadow.querySelector(`[gz-inventory="physicalEndpoints"] [gz-inventory-field="value"]`),
        ],
        section: NavigationConstants.NETWORK_V2.NAME,
        params: {
          filters: this.getPhysicalEndpointsFilters(targets, isPartner),
          treeDetails: {
            targetId: targets[0].companyId,
            type: Constants.Companies.TYPE_COMPANY,
            path: selectedTargetPath,
            parentId: '', // not required
          },
          displayedColumns: this.networkDisplayedColumns,
          recursive: true,
        },
      },
      {
        elements: [
          this.shadow.querySelector(`[gz-inventory="virtualMachines"] [gz-inventory-field="categoryName"]`),
          this.shadow.querySelector(`[gz-inventory="virtualMachines"] [gz-inventory-field="value"]`),
        ],
        section: NavigationConstants.NETWORK_V2.NAME,
        params: {
          filters: this.getVirtualMachinesFilters(targets, isPartner),
          treeDetails: {
            targetId: targets[0].companyId,
            type: Constants.Companies.TYPE_COMPANY,
            path: selectedTargetPath,
            parentId: '', // not required
          },
          displayedColumns: this.networkDisplayedColumns,
          recursive: true,
        },
      },
    ];

    if (availableExtraFeatures.length && availableExtraFeatures.includes('ContainerProtection')) {
      this.addDrillDownHandlersForContainerProtection(targets, navigationItems, isPartner);
    }

    navigationItems = NavigationHelper.removeUnnavigableItems(
      this.dependencies.Ext,
      navigationItems,
      NavigationConstants.NETWORK.NAME
    );

    let navigationHandler;
    if (canDrillDown) {
      navigationHandler = (event, navigationParams) => {
        if (BaseWidgetView.isSuccessState(this.$wrapper as Element)) {
          if (isBrowserFullscreen()) {
            exitFullscreen(() => {}, onExitFullscreenError.bind(this));
          }

          NavigationHelper.navigateToNetworkV2(navigationParams);
        }
      };

      NavigationHelper.addDrillDownEffectsForMultipleElements(
        navigationItems.map(navigationItem => navigationItem.elements)
      );
    }

    NavigationHelper.updateNavigableItems(navigationItems, this.navigationHandlers, navigationHandler);
  }

  /**
   * Add drill down handlers for container and container host
   * @param {Array<Target>} targets
   * @param {Array<NavigationItem>} navigationItems
   * @param isPartner
   */
  addDrillDownHandlersForContainerProtection(
    targets: Array<Target>,
    navigationItems: Array<NavigationItem<InventoryNavigationParams>>,
    isPartner: boolean
  ) {
    const selectedTargetPath = targets[0].path;

    if (!selectedTargetPath) {
      throw new Error('Missing path from target.');
    }

    navigationItems.push(
      {
        elements: [
          this.shadow.querySelector(`[gz-inventory="containerHosts"] [gz-inventory-field="categoryName"]`),
          this.shadow.querySelector(`[gz-inventory="containerHosts"] [gz-inventory-field="value"]`),
        ],
        section: NavigationConstants.NETWORK_V2.NAME,
        params: {
          filters: this.getContainerHostsFilters(targets, isPartner),
          treeDetails: {
            targetId: targets[0].companyId,
            type: Constants.Companies.TYPE_COMPANY,
            path: selectedTargetPath,
            parentId: '', // not required
          },
          displayedColumns: this.networkDisplayedColumns,
          recursive: true,
        },
      },
      {
        elements: [
          this.shadow.querySelector(`[gz-inventory="containers"] [gz-inventory-field="categoryName"]`),
          this.shadow.querySelector(`[gz-inventory="containers"] [gz-inventory-field="value"]`),
        ],
        section: NavigationConstants.NETWORK_V2.NAME,
        params: {
          filters: this.getContainersFilters(targets, isPartner),
          treeDetails: {
            targetId: targets[0].companyId,
            type: Constants.Companies.TYPE_COMPANY,
            path: selectedTargetPath,
            parentId: '', // not required
          },
          displayedColumns: this.networkDisplayedColumns,
          recursive: true,
        },
      }
    );
  }

  private getVirtualMachinesFilters(targets: Array<Target>, isPartner: boolean): Array<GridFilter<NetworkColumn>> {
    const filters: Array<GridFilter<NetworkColumn>> = [
      {
        type: {
          in: [Constants.Network.Filters.Types.VIRTUAL_MACHINES, Constants.Network.Filters.Types.GOLDEN_IMAGES],
        },
      },
      {
        managementStatus: {
          in: [ManagementStatus.MANAGED_NETWORK_V2],
        },
      },
    ];

    if (isPartner) {
      filters.push({
        company: {
          equals: targets[0].companyId,
        },
      });
    }

    return filters;
  }

  private getPhysicalEndpointsFilters(targets: Array<Target>, isPartner: boolean): Array<GridFilter<NetworkColumn>> {
    const filters: Array<GridFilter<NetworkColumn>> = [
      {
        type: {
          in: [Constants.Network.Filters.Types.COMPUTERS],
        },
      },
      {
        managementStatus: {
          in: [ManagementStatus.MANAGED_NETWORK_V2],
        },
      },
    ];

    if (isPartner) {
      filters.push({
        company: {
          equals: targets[0].companyId,
        },
      });
    }

    return filters;
  }

  private getLinuxFilters(targets: Array<Target>, isPartner: boolean): Array<GridFilter<NetworkColumn>> {
    const filters: Array<GridFilter<NetworkColumn>> = [
      {
        type: {
          in: [
            Constants.Network.Filters.Types.COMPUTERS,
            Constants.Network.Filters.Types.VIRTUAL_MACHINES,
            Constants.Network.Filters.Types.GOLDEN_IMAGES,
          ],
        },
      },
      {
        managementStatus: {
          in: [ManagementStatus.MANAGED_NETWORK_V2],
        },
      },
      {
        osType: {
          in: [OperatingSystemType.LINUX],
        },
      },
    ];

    if (isPartner) {
      filters.push({
        company: {
          equals: targets[0].companyId,
        },
      });
    }

    return filters;
  }

  private getMacOsFilters(targets: Array<Target>, isPartner: boolean): Array<GridFilter<NetworkColumn>> {
    const filters: Array<GridFilter<NetworkColumn>> = [
      {
        type: {
          in: [
            Constants.Network.Filters.Types.COMPUTERS,
            Constants.Network.Filters.Types.VIRTUAL_MACHINES,
            Constants.Network.Filters.Types.GOLDEN_IMAGES,
          ],
        },
      },
      {
        managementStatus: {
          in: [ManagementStatus.MANAGED_NETWORK_V2],
        },
      },
      {
        osType: {
          in: [OperatingSystemType.MACOSX],
        },
      },
    ];

    if (isPartner) {
      filters.push({
        company: {
          equals: targets[0].companyId,
        },
      });
    }

    return filters;
  }

  private getWindowsServersFilters(targets: Array<Target>, isPartner: boolean): Array<GridFilter<NetworkColumn>> {
    const filters: Array<GridFilter<NetworkColumn>> = [
      {
        type: {
          in: [
            Constants.Network.Filters.Types.COMPUTERS,
            Constants.Network.Filters.Types.VIRTUAL_MACHINES,
            Constants.Network.Filters.Types.GOLDEN_IMAGES,
          ],
        },
      },
      {
        managementStatus: {
          in: [ManagementStatus.MANAGED_NETWORK_V2],
        },
      },
      {
        osType: {
          in: [OperatingSystemType.WINDOWS],
        },
      },
      {
        endpointType: {
          in: [EndpointType.SERVER],
        },
      },
    ];

    if (isPartner) {
      filters.push({
        company: {
          equals: targets[0].companyId,
        },
      });
    }

    return filters;
  }

  private getWindowsWorkstationsFilters(targets: Array<Target>, isPartner: boolean): Array<GridFilter<NetworkColumn>> {
    const filters: Array<GridFilter<NetworkColumn>> = [
      {
        type: {
          in: [
            Constants.Network.Filters.Types.COMPUTERS,
            Constants.Network.Filters.Types.VIRTUAL_MACHINES,
            Constants.Network.Filters.Types.GOLDEN_IMAGES,
          ],
        },
      },
      {
        managementStatus: {
          in: [ManagementStatus.MANAGED_NETWORK_V2],
        },
      },
      {
        osType: {
          in: [OperatingSystemType.WINDOWS],
        },
      },
      {
        endpointType: {
          in: [EndpointType.WORKSTATION],
        },
      },
    ];

    if (isPartner) {
      filters.push({
        company: {
          equals: targets[0].companyId,
        },
      });
    }

    return filters;
  }

  private getTitleFilters(targets: Array<Target>, isPartner: boolean): Array<GridFilter<NetworkColumn>> {
    const filters: Array<GridFilter<NetworkColumn>> = [
      {
        type: {
          in: [
            Constants.Network.Filters.Types.COMPUTERS,
            Constants.Network.Filters.Types.VIRTUAL_MACHINES,
            Constants.Network.Filters.Types.CONTAINERS,
            Constants.Network.Filters.Types.GOLDEN_IMAGES,
          ],
        },
      },
      {
        managementStatus: {
          in: [ManagementStatus.MANAGED_NETWORK_V2],
        },
      },
    ];

    if (isPartner) {
      filters.push({
        company: {
          equals: targets[0].companyId,
        },
      });
    }

    return filters;
  }

  private getContainersFilters(targets: Array<Target>, isPartner: boolean): Array<GridFilter<NetworkColumn>> {
    const filters: Array<GridFilter<NetworkColumn>> = [
      {
        type: {
          in: [Constants.Network.Filters.Types.CONTAINERS],
        },
      },
    ];

    if (isPartner) {
      filters.push({
        company: {
          equals: targets[0].companyId,
        },
      });
    }

    return filters;
  }

  private getContainerHostsFilters(targets: Array<Target>, isPartner: boolean): Array<GridFilter<NetworkColumn>> {
    const filters: Array<GridFilter<NetworkColumn>> = [
      {
        roleNetworkV2: {
          in: [EntityRole.CONTAINER_HOST],
        },
      },
    ];

    if (isPartner) {
      filters.push({
        company: {
          equals: targets[0].companyId,
        },
      });
    }

    return filters;
  }

  showLoader() {
    this.dom.$inventoryBlocks.forEach(el => {
      this.setTemplateState(el, TemplateState.LOADING);
    });
    this.setTemplateState(this.$wrapper, TemplateState.LOADING);
  }

  hideLoader() {
    this.dom.$inventoryBlocks.forEach(el => {
      this.resetTemplateState(el);
    });
    this.resetTemplateState(this.$wrapper);
  }

  buildSuccessView(response: BackendResponseWithMeta<GetInventoryInformationResponse>) {
    this.resetTemplateState(this.$wrapper);
    let availableExtraFeatures: Array<string> = [];

    const result = response.widgetResult;

    if (result.availableExtraFeatures) {
      availableExtraFeatures = result.availableExtraFeatures;
    }

    if (result.status === Status.EMPTY_STATE) {
      this.buildEmptyTemplate();
      this.setTemplateState(this.$wrapper, TemplateState.EMPTY);
      this.showHideDomElementsDependingOnFeatures(availableExtraFeatures);
      return;
    }

    if (result.status === Status.ACTIVE && result.data) {
      this.latestData = this.format(result.data);
      for (const record in this.latestData) {
        if (this.latestData.hasOwnProperty(record) && this.latestData[record] !== null) {
          this.buildListElement(record);
        }
      }
      this.showHideDomElementsDependingOnFeatures(availableExtraFeatures);

      this.addDrillDownHandlers(
        response[DASHBOARD_METADATA_KEY].targets,
        response[DASHBOARD_METADATA_KEY].canDrillDown,
        availableExtraFeatures
      );

      return;
    }

    this.buildErrorView();
  }

  buildErrorView() {
    this.resetTemplateState(this.$wrapper);
    this.setTemplateState(this.$wrapper, TemplateState.ERROR);
    this.buildEmptyTemplate();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getInventoryWidgetStrings();
  }

  setTranslatedStrings() {
    const { title, labelsMap } = this.translations;
    this.dom.$widgetTitle.innerHTML = `${title}:`;
    this.dom.$winWorkstationsName.innerHTML = labelsMap.winWorkstations;
    this.dom.$winWorkstationsIconTooltip.setAttribute('tooltipText', labelsMap.winWorkstations);
    this.dom.$winServersName.innerHTML = labelsMap.winServers;
    this.dom.$winServersIconTooltip.setAttribute('tooltipText', labelsMap.winServers);
    this.dom.$macOSName.innerHTML = labelsMap.macOs;
    this.dom.$macOSIconTooltip.setAttribute('tooltipText', labelsMap.macOs);
    this.dom.$linuxName.innerHTML = labelsMap.linux;
    this.dom.$linuxIconTooltip.setAttribute('tooltipText', labelsMap.linux);
    this.dom.$physicalEndpointsName.innerHTML = labelsMap.physicalEndpoints;
    this.dom.$physicalEndpointsIconTooltip.setAttribute('tooltipText', labelsMap.physicalEndpoints);
    this.dom.$virtualMachinesName.innerHTML = labelsMap.virtualMachines;
    this.dom.$virtualMachinesIconTooltip.setAttribute('tooltipText', labelsMap.virtualMachines);
    if (this.dom.$containerHostsName) {
      this.dom.$containerHostsName.innerHTML = labelsMap.containerHosts;
      this.dom.$containerHostsIconTooltip.setAttribute('tooltipText', labelsMap.containerHosts);
    }
    if (this.dom.$containersName) {
      this.dom.$containersName.innerHTML = labelsMap.containers;
      this.dom.$containersIconTooltip.setAttribute('tooltipText', labelsMap.containers);
    }
  }

  private buildEmptyTemplate() {
    this.latestData = {
      linux: {
        currentValue: '0',
        delta: '0',
      },
      macOS: {
        currentValue: '0',
        delta: '0',
      },
      physicalEndpoints: {
        currentValue: '0',
        delta: '0',
      },
      virtualMachines: {
        currentValue: '0',
        delta: '0',
      },
      winServers: {
        currentValue: '0',
        delta: '0',
      },
      winWorkstations: {
        currentValue: '0',
        delta: '0',
      },
      containerHosts: {
        currentValue: '0',
        delta: '0',
      },
      containers: {
        currentValue: '0',
        delta: '0',
      },
    };

    for (const record in this.latestData) {
      if (this.latestData.hasOwnProperty(record)) {
        this.buildListElement(record);
      }
    }
  }

  private buildListElement(record) {
    const $valueElement = this.shadow.querySelector(`[gz-inventory="${record}"] [gz-inventory-field="value"]`);
    $valueElement.innerHTML = this.latestData[record].currentValue;
    const $deltaElement = this.shadow.querySelector(`[gz-inventory="${record}"] [gz-inventory-field="delta"]`);
    const deltaValue = this.latestData[record].delta;
    this.displayDeltaInElement($deltaElement, deltaValue, true);
  }

  /**
   * Show or hide dom elements depending on features
   */
  private showHideDomElementsDependingOnFeatures(availableExtraFeatures: string[] = []) {
    const showContainersClass = 'show-containers';

    this.removeElementClass(this.dom.$inventory, showContainersClass);
    if (availableExtraFeatures.length && availableExtraFeatures.includes('ContainerProtection')) {
      this.setElementClass(this.dom.$inventory, showContainersClass);
    }
  }

  private format(data: InventoryInformation): FormattedInventoryInfo {
    return {
      winWorkstations: data.winWorkstations ? this.formatSection(data.winWorkstations) : null,
      winServers: data.winServers ? this.formatSection(data.winServers) : null,
      macOS: data.macOS ? this.formatSection(data.macOS) : null,
      linux: data.linux ? this.formatSection(data.linux) : null,
      physicalEndpoints: data.physicalEndpoints ? this.formatSection(data.physicalEndpoints) : null,
      virtualMachines: data.virtualMachines ? this.formatSection(data.virtualMachines) : null,
      containerHosts: data.containerHosts ? this.formatSection(data.containerHosts) : null,
      containers: data.containers ? this.formatSection(data.containers) : null,
    };
  }

  private formatSection(values: ValueWithDelta) {
    return {
      currentValue: this.formatNumbers(values.currentValue),
      delta: this.formatNumbers(values.delta),
    };
  }

  private formatNumbers(value: number): string {
    return this.localeManager.formatNumber(
      value,
      {
        notation: 'compact',
        compactDisplay: 'short',
      },
      // I.e., en_US -> en-US
      (this.dependencies.getUserLanguage() || '').replace(/_/g, '-')
    );
  }
}
